import { useState, useEffect, DependencyList } from 'react';
import get from 'lodash/get';
import { ValidationError } from '../api/checkErrors';


export type DoNotSet = Symbol;
export type ResourceLoadFn<T> = (abortController: AbortController) => Promise<T | DoNotSet>;

const DONOTSET: DoNotSet = Symbol("DoNotSet");

function useResourceLoader<T>(
  resourceFn: ResourceLoadFn<T>,
  dependencies: DependencyList,
  initialValue?: undefined,
  resourcePath?: string
): [
  T | undefined,
  boolean | undefined,
  Error | undefined,
  React.Dispatch<React.SetStateAction<T | undefined>>
];


function useResourceLoader<T>(
  resourceFn: ResourceLoadFn<T>,
  dependencies: DependencyList,
  initialValue: T,
  resourcePath?: string
): [
  T,
  boolean | undefined,
  Error | undefined,
  React.Dispatch<React.SetStateAction<T>>
];

/** Hook for loading a resource.
 * Loads data as an effect whenever the dependencies change.
 *
 * @param {Function} dataLoadFn - Synchronous or asynchronous function returning the data.
 * @param {DependencyList} dependencies - List of dependencies for the effect. The function is called whenever an item changes in this list.
 * @param {T} initialValue - Initial value of the data.
 *
 * @returns {Array} - An array containing the loaded data, loading state, error state, and a function to set data.
 */
function useResourceLoader<T>(
  resourceFn: ResourceLoadFn<T>,
  dependencies: DependencyList,
  initialValue?: T,
  resourcePath?: string
): [
  T | undefined,
  boolean | undefined,
  Error | undefined,
  React.Dispatch<React.SetStateAction<T | undefined>>
] {
  const [resource, setResource] = useState<T | undefined>(initialValue);
  const [loaderState, setLoaderState] = useState<
    { loading: boolean; error?: Error } | undefined
  >({ loading: true })
  const { loading, error } = loaderState || {}

  useEffect(() => {
    setLoaderState({ loading: true });
    const abortController = new AbortController();
    const flags = { canceled: false, abortController };

    Promise.resolve()
      .then(() => resourceFn(abortController))
      .then(newResource => {
        if (flags.canceled) return;

        if (newResource !== DONOTSET && newResource !== resource) {
          setResource(resourcePath ? get(newResource, resourcePath) : newResource);
        }

        setLoaderState({ loading: false });
      })
      .catch(err => {
        if (flags.canceled) return;

        setLoaderState({
          loading: false,
          error: (err instanceof ValidationError) ? { ...err } : err
        })
      });

    return () => {
      flags.canceled = true;
      flags.abortController.abort();
    };
  }, dependencies);

  return [resource, loading, error, setResource];
}

useResourceLoader.DoNotSet = DONOTSET;

export default useResourceLoader;
